// Firestore persistence for one agreement subcollection. NOT a Vuex module —
// the app has exactly one Vuex store, `src/store.js`, and this consumes it
// (`store.commit('setSaving')`) rather than competing with it.
//
// The schedule lives in a subcollection rather than on the agreement document
// because the pre-flag Holiday Schedule tab (src/holiday-schedule/) reads the
// same doc, which is the rollback path, and because Vuex's
// updateAgreementFields re-reads the whole agreement on every write — one write
// per 15-minute drag increment would make that unusable.
//
// Divii code — this was never part of the client's repo, which persists to
// localStorage. The factory provides the same load/save/clear API backed by
// Firestore at
//   agreements/{agreementId}/{collection}/draft
//
// Skeleton lifted from the proven src/holiday-schedule/store.js: wait for auth,
// serialise concurrent writes, expose flushSave for unmount, and retry a failed
// load once before falling back to seed data.
//
// Write policy lives here rather than in the components: the builders mutate
// their state deeply (a drag fires once per 15-minute increment), so saves are
// debounced and identical payloads are dropped. That keeps the vendored
// components' watchers byte-identical to upstream.

import debounce from 'lodash/debounce';
import firebase from 'firebase/app';
import { agreementsCollection, auth } from '@/firebase';
import store from '@/store';
import { trialActiveForClients, hasEditPermission } from '@/mixins/roleLevelPermissions';

const SAVE_DELAY_MS = 1000;

function isValidCaseId(caseId) {
	return typeof caseId === 'string' && caseId.length > 0 && caseId !== 'default';
}

function waitForAuth(timeoutMs = 15000) {
	if (auth.currentUser) {
		return Promise.resolve(auth.currentUser);
	}
	return new Promise((resolve, reject) => {
		const timer = setTimeout(() => {
			unsubscribe();
			reject(new Error('auth timeout'));
		}, timeoutMs);
		const unsubscribe = auth.onAuthStateChanged((user) => {
			if (user) {
				clearTimeout(timer);
				unsubscribe();
				resolve(user);
			}
		});
	});
}

function beginSaving() {
	store.commit('setSaving', true);
}

function endSaving() {
	setTimeout(() => store.commit('setSaving', false), 500);
}

// Firestore rejects `undefined`; round-tripping through JSON also detaches the
// value from Vue's reactive proxies before it goes over the wire.
function serialize(value) {
	return JSON.parse(JSON.stringify(value));
}

// migrate backfills older stored shapes; seed builds fresh state from load()'s
// extra args; isEmpty decides whether a stored value counts as "nothing yet".
export function createFirestoreDraft({ collection, field, migrate, seed, isEmpty, importLegacy, log }) {
	const draftRef = (caseId) =>
		agreementsCollection.doc(caseId).collection(collection).doc('draft');

	const absent = (raw) => raw == null || isEmpty(raw);

	// One agreement is open at a time, so a single slot of write state per store
	// is enough — load() resets lastJson, so switching agreements is safe.
	// ponytail: key these by caseId if the app ever mounts two agreements at once.
	let lastJson = null;
	let queue = Promise.resolve();

	// A background write that fails is otherwise invisible: the edit stays on
	// screen, so the user believes it is saved and finds out only when they
	// reopen the agreement. The host panel registers a handler to surface it.
	// `action` is 'save' or 'clear'; the panel owns the wording.
	let errorHandler = null;

	function report(action, e) {
		console.error(`${log} Firestore ${action} failed:`, e);
		if (errorHandler) errorHandler(action, e);
	}

	function readOnly() {
		return trialActiveForClients() || !hasEditPermission('parenting-schedule');
	}

	async function writeNow(caseId, value) {
		if (readOnly()) return false;
		await waitForAuth();
		beginSaving();
		try {
			const uid = auth.currentUser ? auth.currentUser.uid : null;
			await draftRef(caseId).set(
				{
					[field]: value,
					updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
					updatedBy: uid,
				},
				{ merge: true }
			);
			return true;
		} finally {
			// Not caught here: every caller either reports the rejection
			// (queueWrite) or expects it (loadOnce's read-only seed write).
			endSaving();
		}
	}

	async function loadOnce(caseId, args) {
		await waitForAuth();

		const snap = await draftRef(caseId).get();
		const raw = snap.exists ? snap.data()[field] : null;

		const imported = importLegacy ? await importLegacy(caseId, raw) : null;
		if (imported) {
			try {
				const written = await writeNow(caseId, serialize(imported.value));
				if (written) await imported.markImported();
			} catch (e) {
				console.warn(`${log} could not persist imported state (read-only user?):`, e);
			}
			return migrate(imported.value);
		}

		if (!absent(raw)) return migrate(raw);

		// First visit: seed and persist so both parties start from the same
		// document. A read-only user will be denied the write — that is fine,
		// they still get the seed for this session.
		const fresh = seed(...args);
		try {
			await writeNow(caseId, serialize(fresh));
		} catch (e) {
			console.warn(`${log} could not persist initial state (read-only user?):`, e);
		}
		return fresh;
	}

	async function load(caseId, ...args) {
		if (!isValidCaseId(caseId)) return seed(...args);

		try {
			return await remember(await loadOnce(caseId, args));
		} catch (e) {
			console.error(`${log} Firestore load failed, retrying once:`, e);
			try {
				await new Promise((r) => setTimeout(r, 750));
				return await remember(await loadOnce(caseId, args));
			} catch (retryError) {
				console.error(`${log} Firestore load retry failed:`, retryError);
				return seed(...args);
			}
		}
	}

	// Record what the document already holds, so the save that echoes back from
	// assigning a freshly loaded value is dropped instead of restamping the doc.
	function remember(value) {
		lastJson = JSON.stringify(value);
		return value;
	}

	// Debounced so a drag is one write, not one per 15-minute increment. lodash
	// invokes with the most recent call's args, and the value is the component's
	// live reactive object, so the payload is always current at fire time.
	const queueWrite = debounce((caseId, value) => {
		const json = JSON.stringify(value);
		if (json === lastJson) return;
		lastJson = json;
		const payload = serialize(value);
		queue = queue
			.then(() => writeNow(caseId, payload))
			.catch((e) => report('save', e));
	}, SAVE_DELAY_MS);

	function save(caseId, value) {
		if (!isValidCaseId(caseId)) return;
		queueWrite(caseId, value);
	}

	/** Write any pending change now and wait for it — call before unmount. */
	async function flushSave() {
		queueWrite.flush();
		await queue;
	}

	async function clear(caseId, ...args) {
		if (!isValidCaseId(caseId)) return seed(...args);
		if (readOnly()) return load(caseId, ...args);

		queueWrite.cancel();
		await queue;
		lastJson = null;

		try {
			await waitForAuth();
			beginSaving();
			await draftRef(caseId).delete();
		} catch (e) {
			report('clear', e);
		} finally {
			endSaving();
		}

		return load(caseId, ...args);
	}

	/** Register the single handler for background write failures. */
	function onError(handler) {
		errorHandler = handler;
	}

	return { load, save, flushSave, clear, onError };
}
